export interface ScheduledPayment {
  method_type_id: number;
  method_label: string;
  processor: string;
  card_brand: string | null;
  card_last_four: string | null;
  charged_automatically: boolean;
  date: string;
  is_past_due: boolean;
}

export type Translate = (key: string, fallback: string, params?: Record<string, string>) => string;

export const CREDIT_CARD_METHOD_ID = 500;

const parseCalendarDate = (value: string): Date | null => {
  const [year, month, day] = value.split(/[ T]/)[0].split('-').map(Number);

  if (![year, month, day].every(Number.isFinite)) return null;

  return new Date(year, month - 1, day);
};

export const formatScheduledDate = (value: string, language: string): string => {
  const date = parseCalendarDate(value);

  return date ? new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date) : value;
};

export const scheduledPaymentSentence = (
  scheduledPayment: ScheduledPayment,
  t: Translate,
  language: string,
): { lead: string; rest: string } => {
  const date = formatScheduledDate(scheduledPayment.date, language);
  const methodId = scheduledPayment.method_type_id;
  const method = t(`invoice-pay:scheduled-payment-method-${methodId}`, scheduledPayment.method_label);
  const payBy = t(`invoice-pay:scheduled-payment-pay-by-${methodId}`, `Pay by ${method}`);
  const card = [
    scheduledPayment.card_brand,
    scheduledPayment.card_last_four ? `•••• ${scheduledPayment.card_last_four}` : null,
  ]
    .filter(Boolean)
    .join(' ');

  if (scheduledPayment.charged_automatically) {
    return {
      lead: card
        ? t('invoice-pay:scheduled-payment-method-with-card', '{{method}}, {{card}}.', { method, card })
        : t('invoice-pay:scheduled-payment-method', '{{method}}.', { method }),
      rest: t('invoice-pay:scheduled-payment-charged-automatically', 'Charged automatically on {{date}}.', { date }),
    };
  }

  if (scheduledPayment.is_past_due) {
    return {
      lead: `${payBy}.`,
      rest: t('invoice-pay:scheduled-payment-was-due', 'Was due {{date}}.', { date }),
    };
  }

  return {
    lead: payBy,
    rest: t('invoice-pay:scheduled-payment-due-by', 'by {{date}}.', { date }),
  };
};
